import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  Renderer2,
  viewChild,
} from '@angular/core';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [ClickOutsideDirective, NgClass],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
})
export class DropdownComponent implements OnInit, OnDestroy {
  isOpen: ModelSignal<boolean> = model<boolean>(false);

  position: 'bottom' | 'top' = 'bottom';
  horizontal: 'left' | 'right' = 'left';
  dropdownMaxHeight = 0;

  opened: OutputEmitterRef<void> = output<void>();
  closed: OutputEmitterRef<void> = output<void>();

  dropdownContentRef = viewChild<ElementRef>('dropdownContent');
  triggerRef = viewChild<ElementRef>('triggerRef');
  private resizeListener = () => {
    if (this.isOpen()) {
      this.updatePosition();
    }
  };
  private readonly _rdr = inject(Renderer2);

  ngOnInit(): void {
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeListener);
  }

  toggle() {
    const opening = !this.isOpen();
    this.isOpen.set(opening);

    if (opening) {
      this.opened.emit();
      setTimeout(() => {
        if (this.dropdownContentRef() && this.triggerRef()) {
          this.updatePosition();
        }
      }, 0);
    } else {
      this.closed.emit();
    }
  }

  close() {
    this.isOpen.set(false);
    this.closed.emit();
  }

  private updatePosition(): void {
    const dropdownContent = this.dropdownContentRef();
    const trigger = this.triggerRef();

    if (!dropdownContent || !trigger) return;

    const triggerEl = trigger.nativeElement as HTMLElement;
    const dropdownEl = dropdownContent.nativeElement as HTMLElement;

    const triggerRect = triggerEl.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    const spaceBelow = windowHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const spaceRight = windowWidth - triggerRect.right;
    const spaceLeft = triggerRect.left;

    const goingBottom = spaceBelow >= spaceAbove;

    this.position = goingBottom ? 'bottom' : 'top';
    this.horizontal = spaceRight >= spaceLeft ? 'left' : 'right';

    const RESERVED_VERTICAL_MARGIN = 90;
    const availableHeight = goingBottom
      ? spaceBelow - RESERVED_VERTICAL_MARGIN
      : spaceAbove - RESERVED_VERTICAL_MARGIN;

    this._rdr.setStyle(
      dropdownEl,
      'max-height',
      `${Math.max(availableHeight, 100)}px`,
    );
    this._rdr.setStyle(dropdownEl, 'overflow-y', 'auto');
  }
}
